import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Upload, CheckCircle, AlertCircle, FileArchive, X } from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

const UploadPortal = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [bookingInfo, setBookingInfo] = useState(null);
  const [uploadCount, setUploadCount] = useState(0);
  const [uploads, setUploads] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPortalInfo();
  }, [token]);

  const fetchPortalInfo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/upload-portal/${token}`);
      setBookingInfo(response.data.booking);
      setUploadCount(response.data.upload_count);
      setUploads(response.data.uploads);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid upload link');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.zip')) {
      toast.error('Only ZIP files are allowed');
      return;
    }

    // Validate file size (1GB max)
    const maxSize = 1 * 1024 * 1024 * 1024; // 1GB
    if (file.size > maxSize) {
      toast.error('File size exceeds 1GB limit');
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);

      await axios.post(`${API}/upload-portal/${token}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log('Upload progress:', percentCompleted + '%');
        }
      });

      toast.success('File uploaded successfully! ✅');
      setSelectedFile(null);
      
      // Refresh portal info
      await fetchPortalInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  const canUpload = uploadCount < 3;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">File Upload Portal</h1>
          <p className="text-purple-200">High Quality Studios - Mix & Master</p>
        </div>

        {/* Booking Info Card */}
        <div className="bg-white rounded-lg shadow-2xl p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Session Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Customer</p>
              <p className="font-semibold text-gray-900">{bookingInfo?.name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Engineer</p>
              <p className="font-semibold text-gray-900">{bookingInfo?.engineer_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Date</p>
              <p className="font-semibold text-gray-900">{bookingInfo?.date}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Time</p>
              <p className="font-semibold text-gray-900">{bookingInfo?.time}</p>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-lg shadow-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">Upload Your Files</h2>
            <div className="text-sm font-semibold">
              <span className={uploadCount >= 3 ? 'text-red-600' : 'text-green-600'}>
                {uploadCount} / 3 Uploads Used
              </span>
            </div>
          </div>

          {canUpload ? (
            <div>
              <div className="border-2 border-dashed border-purple-300 rounded-lg p-8 text-center mb-4">
                {selectedFile ? (
                  <div className="space-y-4">
                    <FileArchive className="w-16 h-16 text-purple-600 mx-auto" />
                    <div>
                      <p className="font-semibold text-gray-900">{selectedFile.name}</p>
                      <p className="text-sm text-gray-600">{formatFileSize(selectedFile.size)}</p>
                    </div>
                    <button
                      onClick={() => setSelectedFile(null)}
                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                    >
                      Remove File
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-2">Drag and drop or click to select</p>
                    <p className="text-sm text-gray-500 mb-4">ZIP files only, max 1GB</p>
                    <label className="inline-block">
                      <input
                        type="file"
                        accept=".zip"
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <span className="cursor-pointer bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition inline-block">
                        Select File
                      </span>
                    </label>
                  </div>
                )}
              </div>

              {selectedFile && (
                <button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Uploading...' : 'Upload File'}
                </button>
              )}

              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Tips:</strong>
                </p>
                <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
                  <li>Compress all your project files into a single ZIP file</li>
                  <li>Include stems, vocals, and any reference tracks</li>
                  <li>Maximum file size: 1GB</li>
                  <li>You can upload up to 3 files total</li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Limit Reached</h3>
              <p className="text-gray-600">You've used all 3 upload slots for this booking.</p>
              <p className="text-sm text-gray-500 mt-2">If you need to upload more files, please contact us.</p>
            </div>
          )}
        </div>

        {/* Uploaded Files List */}
        {uploads.length > 0 && (
          <div className="bg-white rounded-lg shadow-2xl p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Uploaded Files</h2>
            <div className="space-y-3">
              {uploads.map((upload) => (
                <div
                  key={upload.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-semibold text-gray-900">{upload.filename}</p>
                      <p className="text-sm text-gray-600">
                        {formatFileSize(upload.file_size)} • Uploaded {formatDate(upload.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="text-sm font-semibold text-purple-600">
                    Upload #{upload.upload_number}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadPortal;
